// Settings › CRM 2.0 › Customer Consent › Consent report (M1-F6) — as prototype A21t (kReport).
// "Terms: All" or one terms: every acceptance (written by the Customer Portal, M3)
// and the Ask now requests still pending. Download Excel.
import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import { MenuItem, TextField } from "@mui/material";
import Button from "../../../Subcomponents/Button";
import { errorTost } from "../../../Services/Toast";
import { getConsentTerms, getConsentReport, consentError } from "../../../Services/Crm2Consent";
import cssClasses from "../Settings/Crm2Settings.module.css";

const btnSecondary = { width: "fit-content", color: "#344054", backgroundColor: "#FFFFFF", fontSize: "1.4rem", lineHeight: "2rem", whiteSpace: "nowrap" };
const inputSx = { "& .MuiInputBase-input": { fontSize: "1.4rem" } };
const COLS = ["Terms", "Booking", "Who", "Accepted on", "Mobile", "Version", "OTP", "Status"];
const when = (v) => (v ? dayjs(v).format("DD MMM YYYY, hh:mm A") : "—");

function Crm2ConsentReport() {
  const [choices, setChoices] = useState([]); // [{ value: termKey, label: title }]
  const [choice, setChoice] = useState("");
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getConsentTerms()
      .then((terms) => setChoices(terms.map((t) => ({ value: t.termKey, label: t.title }))))
      .catch((err) => {
        console.error("[crm2][Crm2ConsentReport] terms load failed", { message: consentError(err).message });
        errorTost(consentError(err).message);
      });
  }, []);

  useEffect(() => {
    setLoading(true);
    getConsentReport(choice ? { termKey: choice } : {})
      .then(setRows)
      .catch((err) => {
        console.error("[crm2][Crm2ConsentReport] report load failed", { choice, message: consentError(err).message });
        errorTost(consentError(err).message);
      })
      .finally(() => setLoading(false));
  }, [choice]);

  const download = () => {
    const c = choices.find((x) => x.value === choice);
    const sheet = XLSX.utils.json_to_sheet(
      rows.map((r) => ({
        Terms: r.terms, Booking: r.booking, Who: r.who, "Accepted on": when(r.acceptedOn), Mobile: r.mobile, Version: `v${r.version}`, OTP: r.otp, Status: r.status,
      }))
    );
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, "Consent report");
    const name = `Consent_report_${(c?.label || "All_terms").replace(/[^a-z0-9]+/gi, "_")}_${dayjs().format("YYYY-MM-DD")}.xlsx`;
    XLSX.writeFile(book, name);
    console.log("[crm2][Crm2ConsentReport] downloaded", { choice, rows: rows.length, name });
  };

  return (
    <div className={cssClasses.listWrap}>
      <div className={cssClasses.listHead}>
        <TextField select size="small" value={choice} onChange={(e) => setChoice(e.target.value)} sx={{ minWidth: "32rem", ...inputSx }}
          SelectProps={{ displayEmpty: true, renderValue: (v) => `Terms: ${v ? choices.find((c) => c.value === v)?.label : "All"}` }}>
          <MenuItem value="" sx={{ fontSize: "1.4rem" }}>All</MenuItem>
          {choices.map((c) => <MenuItem key={c.value} value={c.value} sx={{ fontSize: "1.4rem" }}>{c.label}</MenuItem>)}
        </TextField>
        <Button style={btnSecondary} btnName="Download Excel" disable={!rows?.length} clicked={download} />
      </div>
      {loading ? (
        <div className={cssClasses.empty}>Loading…</div>
      ) : !rows?.length ? (
        <div className={cssClasses.empty}>No rows for these terms yet.</div>
      ) : (
        <div className={cssClasses.tableCard}>
          <table className={cssClasses.table}>
            <thead><tr>{COLS.map((h) => <th key={h}>{h}</th>)}</tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td>{r.terms}</td><td>{r.booking}</td><td>{r.who}</td><td>{when(r.acceptedOn)}</td><td>{r.mobile}</td><td>v{r.version}</td><td>{r.otp}</td>
                  <td>
                    <span style={{ fontSize: "1.2rem", borderRadius: "1.6rem", padding: "0.2rem 1rem", color: r.status === "Accepted" ? "#027A48" : "#B54708", background: r.status === "Accepted" ? "#ECFDF3" : "#FFFAEB" }}>
                      {r.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className={cssClasses.hint}>
        Saved for every acceptance: the exact text version shown, the consent point, the booking, who (contact person or applicant), time, mobile and that the OTP was
        checked on the server. The OTP value is never saved. Also shown in View CRM Details › Stage 1 Timeline as "Terms accepted".
      </p>
    </div>
  );
}

export default Crm2ConsentReport;
